import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { Loader } from "lucide-react";
import { debounce } from "lodash";
import Select from "../Select";
import { getNodeText } from "../Select/helpers";
import useApi from "../../../../utils/api";
import type { SelectProps } from "../Select/types";
import type { ResourceItem, ResourcePage, ResourceSelectProps } from "./types";

const SEARCH_DEBOUNCE_WAIT = 300;

function ResourceSelect<T extends { id: string | number } = ResourceItem>({
    labelPropName,
    value,
    onChange,
    getSearchFilter,
    resourcePath,
    params,
    renderLabel,
    mode = "multiple",
    enabled = true,
    ...selectProps
}: ResourceSelectProps<T>) {
    const [search, setSearch] = useState("");
    const [querySearch, setQuerySearch] = useState("");
    const { authGet } = useApi();
    const queryClient = useQueryClient();

    const debouncedSetQuerySearch = useMemo(() => {
        return debounce((value: string) => {
            setQuerySearch(value);
            queryClient.removeQueries([resourcePath] as any);
        }, SEARCH_DEBOUNCE_WAIT);
    }, [queryClient, resourcePath]);

    useEffect(() => {
        return () => {
            debouncedSetQuerySearch.cancel();
        };
    }, [debouncedSetQuerySearch]);

    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } = useInfiniteQuery({
        queryKey: [resourcePath, params, querySearch],
        queryFn: async ({ pageParam }): Promise<ResourcePage<T>> => {
            const filters = getSearchFilter ? getSearchFilter(querySearch) : querySearch ? { search: querySearch } : {};
            return authGet(resourcePath, {
                params: {
                    ...filters,
                    ...params,
                    page: pageParam,
                    perPage: 20,
                },
            });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const loadedItems = allPages.reduce((count, page) => count + (page?.result?.length ?? 0), 0);
            return loadedItems < (lastPage?.totalItems ?? 0) ? allPages.length + 1 : undefined;
        },
        refetchOnReconnect: true,
        staleTime: 0,
        enabled,
    });

    const totalItems = data?.pages[data.pages.length - 1]?.totalItems ?? 0;

    const { ref: loadMoreRef, inView } = useInView({ threshold: 0.1 });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const loadedItems = useMemo(() => (data?.pages ?? []).flatMap((page) => page?.result ?? []), [data]);

    // Once we already have every item the backend has for this search, further keystrokes
    // can just filter what's loaded instead of round-tripping for a narrower result set.
    const allItemsLoaded = totalItems > 0 && loadedItems.length >= totalItems;

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        // Clearing (e.g. on close) resets the backend query so the next search fetches again.
        if (!value) {
            debouncedSetQuerySearch.cancel();
            setQuerySearch("");
            return;
        }
        if (!allItemsLoaded) debouncedSetQuerySearch(value);
    }, [allItemsLoaded, debouncedSetQuerySearch]);

    const selectedItems = useMemo(() => {
        if (value == null || typeof value === "string") return [];
        const list = Array.isArray(value) ? value : [value];
        // A reference with no id (e.g. a form default built from a record that doesn't
        // exist yet, `{ id: undefined }`) isn't a real selection - drop it so it never
        // gets looked up as `<resourcePath>/undefined`.
        return list.filter((item) => item?.id != null);
    }, [value]);

    // `null` marks an id that was looked up and doesn't exist (server-side deleted, etc.),
    // so it renders using its partial fallback instead of being retried forever.
    const [hydratedItems, setHydratedItems] = useState<Map<string, T | null>>(new Map());

    // A selected item can be a bare `{ id }` reference (e.g. initial form state) whose
    // full record hasn't loaded yet because it isn't within the first fetched pages.
    // Fetch those individually by id so their label can still be rendered.
    useEffect(() => {
        const missing = selectedItems.filter((selected) => {
            const id = String(selected.id);
            if (loadedItems.some((item) => String(item.id) === id)) return false;
            return !hydratedItems.has(id);
        });

        if (missing.length === 0) return;

        let cancelled = false;

        Promise.all(
            missing.map((selected) =>
                authGet(`${resourcePath}/${selected.id}`).catch(() => null)
            )
        ).then((results) => {
            if (cancelled) return;
            setHydratedItems((prev) => {
                const next = new Map(prev);
                results.forEach((result, index) => {
                    next.set(String(missing[index].id), result ?? null);
                });
                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [selectedItems, loadedItems, hydratedItems, resourcePath, authGet]);

    // Keep selected items resolvable/visible even if they've scrolled out of the loaded pages
    // or came back empty from the by-id lookup - fall back to the partial selected reference
    // so it still shows up (with whatever label info it already carries) instead of disappearing.
    const items = useMemo(() => {
        const list = [...loadedItems];
        selectedItems.forEach((selected) => {
            if (list.some((item) => String(item.id) === String(selected.id))) return;
            list.push(hydratedItems.get(String(selected.id)) ?? selected);
        });
        return list;
    }, [loadedItems, selectedItems, hydratedItems]);

    // Show selected items first, in their selected order, then the rest.
    const sortedItems = useMemo(() => {
        const selectedIds = selectedItems.map((item) => String(item.id));
        const selectedOrder = new Map(selectedIds.map((id, index) => [id, index]));

        return [...items].sort((a, b) => {
            const aIndex = selectedOrder.get(String(a.id));
            const bIndex = selectedOrder.get(String(b.id));
            if (aIndex != null && bIndex != null) return aIndex - bIndex;
            if (aIndex != null) return -1;
            if (bIndex != null) return 1;
            return 0;
        });
    }, [items, selectedItems]);

    const itemsById = useMemo(() => new Map(items.map((item) => [String(item.id), item])), [items]);


    const selectValue = useMemo(() => {
        if (mode === "multiple") return selectedItems.map((item) => String(item.id));
        return selectedItems[0] ? String(selectedItems[0].id) : undefined;
    }, [mode, selectedItems]);

    const renderResourceLabel = useCallback((item: T): ReactNode => {
        const record = item as Record<string, unknown>;
        if (renderLabel) {
            return renderLabel(item);
        } else if (labelPropName && record?.[labelPropName]) {
            return record[labelPropName] as ReactNode;
        } else {
            return (record?.code || record?.description || record?.name) as ReactNode;
        }
    }, [renderLabel, labelPropName]);

    // With everything loaded, `querySearch` stays behind what's typed, so filter locally
    // against the already-loaded items instead of waiting on/triggering another fetch.
    // Selected items always stay visible even if they don't match the current search.
    const displayedItems = useMemo(() => {
        if (!allItemsLoaded || !search.trim()) return sortedItems;
        const query = search.trim().toLowerCase();
        const selectedIds = new Set(selectedItems.map((item) => String(item.id)));
        return sortedItems.filter(
            (item) => selectedIds.has(String(item.id)) || getNodeText(renderResourceLabel(item)).toLowerCase().includes(query)
        );
    }, [sortedItems, allItemsLoaded, search, selectedItems, renderResourceLabel]);

    const handleChange = (nextValue?: string | string[]) => {
        if (nextValue == null) {
            onChange?.(mode === "multiple" ? [] : undefined);
            return;
        }
        if (Array.isArray(nextValue)) {
            onChange?.(nextValue.map(
                (id) => itemsById.get(id)).filter((item): item is T => item != null)
            );
            return;
        }
        onChange?.(itemsById.get(nextValue));
    };

    return (
        <Select
            {...(selectProps as Omit<SelectProps, "children" | "mode" | "value" | "onChange" | "searchValue" | "onSearchChange">)}
            mode={mode as "single"}
            value={selectValue as string}
            onChange={handleChange as (value?: string) => void}
            searchValue={search}
            onSearchChange={handleSearchChange}
        >
            {displayedItems.map((item) => (
                <Select.Item key={String(item.id)} value={String(item.id)}>
                    {renderResourceLabel(item)}
                </Select.Item>
            ))}
            {isLoading && (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-2">
                    <Loader className="tw-h-4 tw-w-4 tw-animate-spin tw-text-muted-foreground" />
                </div>
            )}
            {hasNextPage && (
                <div ref={loadMoreRef} className="tw-flex tw-items-center tw-justify-center tw-py-2">
                    {isFetchingNextPage && (
                        <Loader
                            role="status"
                            className="tw-h-4 tw-w-4 tw-animate-spin tw-text-muted-foreground tw-inline-block tw-origin-center"
                            strokeLinecap="round"
                        />
                    )}
                </div>
            )}
        </Select>
    );
}

export default ResourceSelect;
